import { Component, computed, inject, OnInit, signal } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import {
  PmDialogComponent,
  PmTabsGroupComponent,
  SelectTabOption,
} from '@libs/ui';
import { ANALYSIS_EXPORT_KIND } from '@libs/shared/validators/analysis-export.validator';
import { TranslateModule } from '@ngx-translate/core';
import { ExportCenterService } from 'common/export-center/export-center.service';
import { ExportDownloadsListComponent } from 'common/export-center/export-downloads-list.component';
import { AnalysisStateService } from 'demand-forecasting/analysis/services/analysis-state.service';

export enum ExportTab {
  NEW = 'new',
  LIST = 'list',
}

export interface AnalysisExportDialogData {
  initialTab?: ExportTab;
  highlightExportId?: string;
}

@Component({
  templateUrl: './analysis-export-dialog.component.html',
  imports: [
    PmDialogComponent,
    PmTabsGroupComponent,
    ExportDownloadsListComponent,
    TranslateModule,
  ],
})
export class AnalysisExportDialogComponent implements OnInit {
  protected readonly dialogRef = inject(
    MatDialogRef<AnalysisExportDialogComponent>,
  );
  protected readonly dialogData = inject<AnalysisExportDialogData | null>(
    MAT_DIALOG_DATA,
    { optional: true },
  );
  private readonly exportCenter = inject(ExportCenterService);
  private readonly analysisStateService = inject(AnalysisStateService);

  protected readonly ExportTab = ExportTab;

  protected readonly exports = this.exportCenter.listResource(
    ANALYSIS_EXPORT_KIND,
    () => this.analysisStateService.selectedVersion()?.id,
  );

  protected activeTab = signal<ExportTab>(
    this.dialogData?.initialTab ?? ExportTab.NEW,
  );

  protected readonly tabOptions: SelectTabOption[] = [
    { value: ExportTab.NEW, label: 'ANALYSIS.EXPORT.TAB_NEW', disabled: false },
    {
      value: ExportTab.LIST,
      label: 'ANALYSIS.EXPORT.TAB_LIST',
      disabled: false,
    },
  ];

  protected selectedExportId = signal<string | undefined>(
    this.dialogData?.highlightExportId,
  );

  protected primaryCtaText = computed(() => {
    switch (this.activeTab()) {
      case ExportTab.LIST:
        return 'COMMON.DOWNLOAD';
      case ExportTab.NEW:
        return 'ANALYSIS.EXPORT.START';
    }
  });

  protected primaryCtaDisabled = computed(() => {
    if (this.activeTab() === ExportTab.LIST) {
      return !this.selectedExportId();
    }
    return this.exportCenter.startPending();
  });

  protected readonly primaryCtaButtonConfig = computed(() => ({
    text: this.primaryCtaText(),
    disabled: this.primaryCtaDisabled(),
    handleClick: () => this.handlePrimaryCtaClick(),
  }));

  ngOnInit(): void {
    this.activeTab.set(this.dialogData?.initialTab ?? ExportTab.NEW);
  }

  protected handlePrimaryCtaClick(): void {
    if (this.activeTab() === ExportTab.LIST) {
      this.downloadSelected();
      return;
    }
    this.startExport();
  }

  protected setActiveTab(tab: string): void {
    this.activeTab.set(tab as ExportTab);
  }

  private downloadSelected(): void {
    const exportId = this.selectedExportId();
    if (!exportId) {
      return;
    }
    this.exportCenter
      .download(exportId)
      .subscribe({ complete: () => this.dialogRef.close() });
  }

  private startExport(): void {
    const selectedVersion = this.analysisStateService.selectedVersion();
    if (!selectedVersion) {
      return;
    }
    this.exportCenter
      .start(ANALYSIS_EXPORT_KIND, { analysisVersionId: selectedVersion.id })
      .subscribe({
        next: () => this.dialogRef.close(),
        error: () => undefined,
      });
  }
}
